import * as React from "react";
import { useSearchParams } from "react-router-dom";

import {
  createEmptyLayout,
  isTemporaryLayout,
  LayoutRecord,
} from "@/shared/domain/layouts";
import { SearchParams } from "@/shared/environment";

import { useWorkspaceLoaderData } from "../loader";

interface SetActiveLayoutOptions {
  skipReload?: boolean;
}

/**
 * Visualizer layout app state
 */
export interface LayoutState {
  activeLayout: LayoutRecord;
  setActiveLayout: (
    layout: LayoutRecord,
    options?: SetActiveLayoutOptions,
  ) => void;
  isLoading: boolean;
  error: Error | null;
  skipLayoutRehydration: boolean;
}

export function useLayoutState(): LayoutState {
  const [_, setSearchParams] = useSearchParams();
  const workspaceResult = useWorkspaceLoaderData();

  // Active layout state
  const [activeLayout, setActiveLayoutState] = React.useState<LayoutRecord>(
    () => {
      // If we have a layout from the loader data, use that
      if (workspaceResult.configSource?.type === "layout") {
        return workspaceResult.configSource.value;
      }

      // Otherwise, use the initial layout
      return createEmptyLayout();
    },
  );

  // Flag to skip the next layout reload. Used when creating a new layout
  const [skipLayoutRehydration, setSkipLayoutRehydration] =
    React.useState(false);

  const setActiveLayout = React.useCallback(
    (layout: LayoutRecord, options?: SetActiveLayoutOptions) => {
      setSkipLayoutRehydration(options?.skipReload ?? false);
      setActiveLayoutState(layout);
    },
    [],
  );

  React.useEffect(
    function syncActiveLayoutToUrl() {
      // Sync URL parameter with layout name
      if (isTemporaryLayout(activeLayout)) {
        // Remove layout name from URL if it's the temporary layout
        setSearchParams(
          (oldParams) => {
            const params = new URLSearchParams(oldParams);
            params.delete(SearchParams.LAYOUT);
            return params;
          },
          { replace: true },
        );
      } else {
        // Otherwise, sync URL with the new layout name
        setSearchParams(
          (oldParams) => {
            const params = new URLSearchParams(oldParams);
            params.set(SearchParams.LAYOUT, activeLayout.name);
            return params;
          },
          { replace: true },
        );
      }
    },
    [activeLayout, setSearchParams],
  );

  return {
    activeLayout: activeLayout,
    error: null,
    isLoading: false,
    setActiveLayout,
    skipLayoutRehydration,
  };
}
